import { calculateDocumentWeightedScore } from '@core/util/documents';
import { GlobalWordDocumentTemplate, GroupType } from '@models/documents';
import {
	GlobalGenericFormTemplate,
	GlobalMortgageFormTemplate,
} from '@models/global-forms';
import { MatterFile } from '@models/matter-file';
import { MatterPackageContentType } from '@models/matter-package';
import { Selector } from '@ngxs/store';
import {
	MatterPackagesSelectors,
	MatterPackageWithStatuses,
} from '@state/matter/matter-packages/matter-packages.selectors';
import {
	MatterWordDocumentSelectors,
	MatterWordDocumentWithMetadata,
} from '@state/matter/matter-word-documents/matter-word-documents.selectors';
import {
	UserMattersUsageState,
	UserUsageMatter,
	UserUsageMatterWordDocument,
} from '@state/user-usage-matters/user-usage-matters.state';
import { BusinessFeatureFlagSelectors } from 'src/app/shared/state/business';
import { BusinessWordDocumentsState } from 'src/app/shared/state/business/word/business-word-documents/business-word-documents.state';
import { GenericFormsTemplatesState } from 'src/app/shared/state/generic-forms/state';
import { VirtualizedMatterFile } from 'src/app/shared/state/global/uploaded-documents/upload-documents';
import { MatterMortgageFormTemplatesState } from 'src/app/shared/state/matter/matter_mortgages/matter-mortgage-forms/matter-mortgage-form-templates.state';
import { MatterUploadedFilesSelectors } from 'src/app/shared/state/matter/uploaded-files/matter-uploaded-files.selectors';

export interface MatterPackageWithStatusAndDocuments
	extends MatterPackageWithStatuses {
	syncMortgageId?: string;
	documents: (MatterWordDocumentWithMetadata & {
		contentType: MatterPackageContentType;
	})[];
}

export interface DocumentUsageByType {
	count: number;
	lastUsedAt?: number;
	weightedScore: number;
}

export interface DocumentUsageData {
	lastUsedAt?: number;
	weightedScore?: number;
	usageByType: {
		[matterType: string]: DocumentUsageByType;
	};
}

export interface DocumentUsageStats {
	[documentSource: string]: DocumentUsageData;
}

export interface KeywordLabeledTemplate extends GlobalWordDocumentTemplate {
	keywords: string[];
	contentType: MatterPackageContentType;
}

export interface KeywordLabeledFormTemplate {
	id: string;
	name: string;
	currentVersion: string;
	context: string;
	fileType: string | null;
	groupType: string | null;
	keywords: string[];
	/**
	 * The source is the same as the {@link id} for form templates. Used for the Template Engine
	 */
	source: string;
	contentType: MatterPackageContentType;
}
export interface MatterFileWithKeywords extends MatterFile {
	keywords: string[];
	fileType: string | null;
	groupType: string | null;
	source: string | null;
	contentType: MatterPackageContentType;
}

export interface UploadedVoidCheque extends VirtualizedMatterFile {
	keywords: string[];
	fileType: string | null;
	groupType: string | null;
	source: string | null;
	contentType: MatterPackageContentType;
}

export class DocumentsV2Selectors {
	@Selector([
		MatterPackagesSelectors.getPackagesWithStatuses,
		MatterWordDocumentSelectors.getDocumentsWithStatuses,
	])
	static getPackagesWithStatusesAndDocuments(
		packages: MatterPackageWithStatuses[],
		documents: MatterWordDocumentWithMetadata[],
	): MatterPackageWithStatusAndDocuments[] {
		return (
			packages?.map((item) => {
				const sortedContents = [...(item.contents ?? [])].sort(
					(a, b) => a.ordinal - b.ordinal,
				);
				return {
					...item,
					documents: sortedContents
						.map((c) => {
							const doc = documents.find((d) => d.document.id === c.id);
							if (!doc) return undefined;
							return {
								...doc,
								contentType: c.contentType,
							};
						})
						.filter(
							(
								doc,
							): doc is MatterWordDocumentWithMetadata & {
								contentType: MatterPackageContentType;
							} => doc !== undefined,
						),
				};
			}) || []
		);
	}

	@Selector([
		MatterWordDocumentSelectors.getDocumentsWithStatuses,
		MatterPackagesSelectors.getPackagesWithStatuses,
	])
	static getDocumentsWithStatusesAndPackages(
		documents: MatterWordDocumentWithMetadata[],
		packages: MatterPackageWithStatuses[],
	): MatterWordDocumentWithMetadata[] {
		return documents
			.map((item) => ({
				...item,
				includedInPackages: packages.filter((pkg) =>
					(pkg.contents || []).some(
						(content) => content.id === item.document.id,
					),
				),
			}))
			.filter((item) => item.document?.title)
			.sort((a, b) => {
				const aDate = a.document?.updatedAt || a.document?.createdAt;
				const bDate = b.document?.updatedAt || b.document?.createdAt;
				if (!aDate && !bDate) return 0;
				if (!aDate) return 1;
				if (!bDate) return -1;
				return bDate - aDate;
			});
	}

	@Selector([DocumentsV2Selectors.getPackagesWithStatusesAndDocuments])
	static getPackageById(
		packages: MatterPackageWithStatusAndDocuments[],
	): (packageId: string) => MatterPackageWithStatusAndDocuments | undefined {
		return (packageId: string) => packages.find((pkg) => pkg.id === packageId);
	}

	@Selector([
		UserMattersUsageState.userUsageMatters,
		UserMattersUsageState.userUsageMatterWordDocuments,
	])
	static getDocumentUsageStats(
		matters: UserUsageMatter[],
		matterWordDocuments: { [matterId: string]: UserUsageMatterWordDocument[] },
	): DocumentUsageStats {
		const documentUsage: DocumentUsageStats = {};

		matters.forEach((matter) => {
			const matterDocuments = matterWordDocuments[matter.id] || [];
			const matterType = matter.fileType || 'unknown';

			matterDocuments.forEach((document) => {
				const docSource = document.source || 'unknown';
				const documentDate =
					document.lastEditedAt || document.createdAt || matter.createdAt;

				if (!documentUsage[docSource]) {
					documentUsage[docSource] = {
						usageByType: {},
					};
				}

				if (!documentUsage[docSource].usageByType[matterType]) {
					documentUsage[docSource].usageByType[matterType] = {
						count: 0,
						weightedScore: 0,
					};
				}

				const usageByType = documentUsage[docSource].usageByType[matterType];
				usageByType.count++;

				if (documentDate) {
					if (
						!documentUsage[docSource].lastUsedAt ||
						documentDate > (documentUsage[docSource].lastUsedAt ?? 0)
					) {
						documentUsage[docSource].lastUsedAt = documentDate;
					}
					if (
						!usageByType.lastUsedAt ||
						documentDate > (usageByType.lastUsedAt ?? 0)
					) {
						usageByType.lastUsedAt = documentDate;
					}
				}
			});
		});

		Object.values(documentUsage).forEach((docData) => {
			let totalWeightedScore = 0;

			Object.values(docData.usageByType).forEach((usageByType) => {
				if (usageByType.lastUsedAt) {
					usageByType.weightedScore = calculateDocumentWeightedScore(
						usageByType.lastUsedAt,
						usageByType.count,
					);
					totalWeightedScore += usageByType.weightedScore;
				}
			});

			docData.weightedScore = totalWeightedScore;
		});

		return documentUsage;
	}

	@Selector([
		MatterMortgageFormTemplatesState.getFormTemplates,
		GenericFormsTemplatesState.getGenericFormTemplates,
		BusinessFeatureFlagSelectors.isSuggestedPackagesEnabled,
	])
	static labeledFormTemplates(
		templates: GlobalMortgageFormTemplate[],
		genericTemplates: GlobalGenericFormTemplate[],
		isSuggestedPackagesEnabled: boolean,
	): KeywordLabeledFormTemplate[] {
		if (!isSuggestedPackagesEnabled) {
			return [];
		}

		const templatesWithContentType = templates.map((template) => ({
			...template,
			contentType: MatterPackageContentType.SYNC_FORM,
		}));
		const genericTemplatesWithContentType = genericTemplates.map(
			(template) => ({
				...template,
				contentType: MatterPackageContentType.MATTER_FORM,
			}),
		);

		return [
			...templatesWithContentType,
			...genericTemplatesWithContentType,
		].map((template) => {
			const labels = Object.entries(template.labels || {}).flatMap(
				([key, value]) => {
					if (Array.isArray(value)) {
						return value.map((v) => `${key}:${v}`);
					} else if (typeof value === 'object' && value !== null) {
						return Object.entries(value).map(([k, v]) => `${key}:${k}:${v}`);
					}
					return [`${key}:${value}`];
				},
			);
			return {
				...template,
				source: template.id,
				keywords: [
					'contentType:formTemplate',
					`template:${template.id}`,
					...labels,
					...Object.keys(template.keywords || {}),
				],
				fileType: null,
				groupType: null,
			};
		});
	}

	@Selector([
		BusinessWordDocumentsState.getAllMerged,
		BusinessFeatureFlagSelectors.isSuggestedPackagesEnabled,
	])
	static wordTemplates(
		templates: GlobalWordDocumentTemplate[],
		isSuggestedPackagesEnabled: boolean,
	): KeywordLabeledTemplate[] {
		if (!isSuggestedPackagesEnabled) {
			return [];
		}

		return templates.map((template) => {
			const contextKeyword = this.getGroupTypeKeyword(template.groupType);
			const keywords: string[] = [
				'contentType:documentTemplate',
				`template:${template.source}`,
			];
			if (contextKeyword) {
				keywords.push(contextKeyword);
			}
			return {
				...template,
				keywords,
				contentType: MatterPackageContentType.MATTER_DOC,
			};
		});
	}

	@Selector([
		MatterUploadedFilesSelectors.getAllUploadedFiles,
		BusinessFeatureFlagSelectors.isSuggestedPackagesEnabled,
	])
	static uploadedFilesKeywords(
		uploadedFiles: MatterFile[],
		isSuggestedPackagesEnabled: boolean,
	): MatterFileWithKeywords[] {
		if (!isSuggestedPackagesEnabled) {
			return [];
		}

		try {
			return uploadedFiles.map((file) => {
				return {
					...file,
					keywords: [
						'contentType:uploadedFile',
						`file:${file.id}`,
						...(file.systemTags || []),
						...(file.keywords || []),
					],
					fileType: file.fileType,
					source: null,
					groupType: null,
					contentType: MatterPackageContentType.UPLOADED_FILE,
				};
			});
		} catch (error) {
			console.error('Error mapping uploaded files to keywords', error);
			return [];
		}
	}

	@Selector([
		MatterWordDocumentSelectors.mapVoidChequesToWordWithMeta,
		BusinessFeatureFlagSelectors.isSuggestedPackagesEnabled,
	])
	static uploadedVoidCheques(
		voidCheques: MatterWordDocumentWithMetadata[],
		isSuggestedPackagesEnabled: boolean,
	): MatterWordDocumentWithMetadata[] {
		if (!isSuggestedPackagesEnabled) {
			return [];
		}

		return voidCheques.map((voidCheque) => {
			return {
				...voidCheque,
				keywords: [
					'contentType:uploadedFile',
					`file:voidCheque`,
					`trustAccount:${voidCheque.document.source}`,
				],
				contentType: MatterPackageContentType.UPLOADED_FILE,
			};
		});
	}

	/**
	 * Converts GroupType enum values to keywords
	 */
	private static getGroupTypeKeyword(groupType: GroupType): string | null {
		const groupTypeMap: Record<string, string> = {
			[GroupType.MORTGAGE]: 'mortgage',
			[GroupType.DEBT]: 'debt',
			[GroupType.ENCUMBRANCE]: 'encumbrance',
			[GroupType.JURISDICTION]: 'jurisdiction',
			[GroupType.JURISDICTION_TAX_DEPARTMENT]: 'jurisdiction',
			[GroupType.JURISDICTION_ZONING_DEPARTMENT]: 'jurisdiction',
			[GroupType.JURISDICTION_FIRE_DEPARTMENT]: 'jurisdiction',
			[GroupType.JURISDICTION_WATER_DEPARTMENT]: 'jurisdiction',
			[GroupType.AFFIDAVIT]: 'client',
			[GroupType.REMAIN]: 'encumbrance',
		};

		return groupTypeMap[groupType] || null;
	}
}
